import { ProductName, FormMode } from "module/interfaces/formInterface";
import { subscriptionActions } from "./actions";
import {
  IGeoLocationResponse,
  ISubscriptionAccount,
  ISubscriptionProduct,
  ISubscriptionState,
  ITransactionMap,
  InvoiceData,
  transactionData,
} from "./interface";

export const subscriptionInitialState: ISubscriptionState = {
  productSubscriptionMap: {},
  subscriptionAccountsMap: {},
  invoiceList: [],
  invoice: {},
  transactions: [],
  transactionMap: {},
  subscriptionAccountData: {},
  activeProductName: ProductName.forms,
  activeFormMode: FormMode.view,
  userLocation: {
    geoplugin_request: "",
    geoplugin_status: 0,
    geoplugin_delay: "",
    geoplugin_credit: "",
    geoplugin_city: "",
    geoplugin_region: "",
    geoplugin_regionCode: "",
    geoplugin_regionName: "",
    geoplugin_areaCode: "",
    geoplugin_dmaCode: "",
    geoplugin_countryCode: "",
    geoplugin_countryName: "",
    geoplugin_inEU: 0,
    geoplugin_euVATrate: false,
    geoplugin_continentCode: "",
    geoplugin_continentName: "",
    geoplugin_latitude: "",
    geoplugin_longitude: "",
    geoplugin_locationAccuracyRadius: "",
    geoplugin_timezone: "",
    geoplugin_currencyCode: "",
    geoplugin_currencySymbol: "",
    geoplugin_currencySymbol_UTF8: "",
    geoplugin_currencyConverter: 0,
  },
  isCurrentLoggedUserSubscribed: false,
  loaders: {
    fetchInvoice: false,
    fetchAccount: false,
    fetchCatalogue: false,
    checkSubscriptionAccountById: false,
    createSubscriptionAccount: false,
    updateSubscriptionAccount: false,
    userLocation: false,
  },
  isFailed: {
    userLocation: false,
    fetchInvoice: false,
    fetchAccount: false,
    fetchCatalogue: false,
    checkSubscriptionAccountById: false,
    createSubscriptionAccount: false,
    updateSubscriptionAccount: false,
  },
  errors: {
    fetchInvoice: "",
    fetchAccount: "",
    fetchCatalogue: "",
    checkSubscriptionAccountById: "",
    createSubscriptionAccount: "",
    updateSubscriptionAccount: "",
  },
  userSubscriptions: [],
  userSubscriptionsLoading: false,
  userSubscriptionsError: null,
  subscriptionInvoices: [],
  userSubscriptionStatus: [],
  userSubscriptionStatusSuccess: null,
  planSubscriptionStatus: {},
  planSubscriptionStatusSuccess: null
};

export const subscriptionReducer = (
  state: ISubscriptionState = subscriptionInitialState,
  action: { type: string; payload: any }
) => {
  switch (action.type) {
    case subscriptionActions.FETCH_USER_LOCATION_SUCCESS: {
      const payload = action.payload as IGeoLocationResponse;
      return {
        ...state,
        userLocation: payload,
        loaders: {
          ...state.loaders,
          userLocation: false,
        },
        isFailed: {
          ...state.isFailed,
          userLocation: false,
        },
      };
    }
    case subscriptionActions.FETCH_USER_LOCATION: {
      return {
        ...state,
        loaders: {
          ...state.loaders,
          userLocation: true,
        },
        isFailed: {
          ...state.isFailed,
          userLocation: false,
        },
      };
    }
    case subscriptionActions.FETCH_USER_LOCATION_FAILURE: {
      return {
        ...state,
        loaders: {
          ...state.loaders,
          userLocation: false,
        },
        isFailed: {
          ...state.isFailed,
          userLocation: true,
        },
      };
    }
    case subscriptionActions.FETCH_CATALOGUE: {
      return {
        ...state,
        loaders: {
          ...state.loaders,
          fetchCatalogue: true,
        },
        errors: {
          ...state.errors,
          fetchCatalogue: "",
        },
      };
    }
    case subscriptionActions.FETCH_INVOICE: {
      return {
        ...state,
        loaders: {
          ...state.loaders,
          fetchInvoice: true,
        },
        errors: {
          ...state.errors,
          fetchInvoice: "",
        },
      };
    }
    case subscriptionActions.FETCH_INVOICE_SUCCESS: {
      const invoice: InvoiceData = action.payload?.invoice || {};
      return {
        ...state,
        invoice: invoice,
        loaders: {
          ...state.loaders,
          fetchInvoice: false,
        },
        errors: {
          ...state.errors,
          fetchInvoice: "",
        },
      };
    }
    case subscriptionActions.FETCH_CATALOGUE_SUCCESS: {
      const products: ISubscriptionProduct[] = action.payload?.products || [];
      const productSubscriptionMap = { ...state.productSubscriptionMap };

      products.forEach((product) => {
        const productName = product?.name || "";
        productSubscriptionMap[productName] = product;
      });
      return {
        ...state,
        productSubscriptionMap: productSubscriptionMap,
        loaders: {
          ...state.loaders,
          fetchCatalogue: false,
        },
        errors: {
          ...state.errors,
          fetchCatalogue: "",
        },
      };
    }
    case subscriptionActions.FETCH_INVOICE_FAILURE: {
      return {
        ...state,
        loaders: {
          ...state.loaders,
          fetchInvoice: false,
        },
        errors: {
          ...state.errors,
          fetchInvoice: action.payload,
        },
      };
    }
    case subscriptionActions.FETCH_CATALOGUE_FAILURE: {
      return {
        ...state,
        loaders: {
          ...state.loaders,
          fetchCatalogue: false,
        },
        errors: {
          ...state.errors,
          fetchCatalogue: action.payload,
        },
      };
    }

    case subscriptionActions.CHECK_SUBSCRIPTION_ACCOUNT_BY_ID: {
      return {
        ...state,
        loaders: {
          ...state.loaders,
          checkSubscriptionAccountById: true,
        },
        errors: {
          ...state.errors,
          checkSubscriptionAccountById: "",
        },
      };
    }

    case subscriptionActions.CHECK_SUBSCRIPTION_ACCOUNT_BY_ID_SUCCESS: {
      const subscriptionAccountsMap = { ...state.subscriptionAccountsMap };
      const subscriptionAccountData: ISubscriptionAccount = action.payload;
      subscriptionAccountsMap[subscriptionAccountData?.externalKey] =
        subscriptionAccountData;
      return {
        ...state,
        isCurrentLoggedUserSubscribed: true,
        subscriptionAccountsMap: subscriptionAccountsMap,
        loaders: {
          ...state.loaders,
          checkSubscriptionAccountById: false,
        },
        errors: {
          ...state.errors,
          checkSubscriptionAccountById: "",
        },
      };
    }

    case subscriptionActions.CHECK_SUBSCRIPTION_ACCOUNT_BY_ID_FAILURE: {
      return {
        ...state,
        isCurrentLoggedUserSubscribed: false,
        loaders: {
          ...state.loaders,
          checkSubscriptionAccountById: false,
        },
        errors: {
          ...state.errors,
          checkSubscriptionAccountById: action.payload,
        },
      };
    }

    case subscriptionActions.CREATE_SUBSCRIPTION_ACCOUNT: {
      return {
        ...state,
        loaders: {
          ...state.loaders,
          createSubscriptionAccount: true,
        },
        errors: {
          ...state.errors,
          createSubscriptionAccount: "",
        },
      };
    }

    case subscriptionActions.CREATE_SUBSCRIPTION_ACCOUNT_SUCCESS: {
      return {
        ...state,
        isCurrentLoggedUserSubscribed: true,
        loaders: {
          ...state.loaders,
          createSubscriptionAccount: false,
        },
        errors: {
          ...state.errors,
          createSubscriptionAccount: "",
        },
      };
    }

    case subscriptionActions.CREATE_SUBSCRIPTION_ACCOUNT_FAILURE: {
      return {
        ...state,
        loaders: {
          ...state.loaders,
          createSubscriptionAccount: false,
        },
        errors: {
          ...state.errors,
          createSubscriptionAccount: action.payload,
        },
      };
    }

    case subscriptionActions.UPDATE_SUBSCRIPTION_ACCOUNT: {
      return {
        ...state,
        loaders: {
          ...state.loaders,
          updateSubscriptionAccount: true,
        },
        errors: {
          ...state.errors,
          updateSubscriptionAccount: "",
        },
      };
    }

    case subscriptionActions.UPDATE_SUBSCRIPTION_ACCOUNT_SUCCESS: {
      return {
        isCurrentLoggedUserSubscribed: true,
        ...state,
        loaders: {
          ...state.loaders,
          updateSubscriptionAccount: false,
        },
        errors: {
          ...state.errors,
          updateSubscriptionAccount: "",
        },
      };
    }

    case subscriptionActions.UPDATE_SUBSCRIPTION_ACCOUNT_FAILURE: {
      return {
        ...state,
        loaders: {
          ...state.loaders,
          updateSubscriptionAccount: false,
        },
        errors: {
          ...state.errors,
          updateSubscriptionAccount: action.payload,
        },
      };
    }
    case subscriptionActions.UPDATE_INVOICE_LIST: {
      const invoiceList = action.payload;
      return {
        ...state,
        invoiceList,
      };
    }
    case subscriptionActions.FETCH_INVOICE_PAYMENTS_SUCCESS: {
      const paymentsList = action.payload;
      const transactionMap: ITransactionMap = { ...state.transactionMap };
      paymentsList?.forEach((item: transactionData) => {
        transactionMap[item.targetInvoiceId] = item;
      });

      return {
        ...state,
        transactions: paymentsList,
        transactionMap: transactionMap,
      };
    }

    case subscriptionActions.FETCH_ACCOUNT_DETAILS: {
      return {
        ...state,
        loaders: {
          ...state.loaders,
          fetchAccount: true,
        },
        errors: {
          ...state.errors,
          fetchAccount: "",
        },
      };
    }
    case subscriptionActions.FETCH_ACCOUNT_DETAILS_SUCCESS: {
      const subscriptionAccountData =
        action.payload || state.subscriptionAccountData;
      return {
        ...state,
        subscriptionAccountData: subscriptionAccountData,
        loaders: {
          ...state.loaders,
          fetchAccount: false,
        },
        errors: {
          ...state.errors,
          fetchAccount: "",
        },
      };
    }
    case subscriptionActions.FETCH_ACCOUNT_DETAILS_FAILURE: {
      return {
        ...state,
        loaders: {
          ...state.loaders,
          fetchAccount: false,
        },
        errors: {
          ...state.errors,
          fetchAccount: action.payload,
        },
      };
    }
    case subscriptionActions.SET_ACTIVE_PRODUCT_NAME: {
      return {
        ...state,
        activeProductName: action.payload,
      };
    }
    case subscriptionActions.SET_ACTIVE_FORM_MODE: {
      return {
        ...state,
        activeFormMode: action.payload,
      };
    }
    case subscriptionActions.FETCH_USER_SUBSCRIPTIONS:
      return {
        ...state,
        userSubscriptionsLoading: true,
        userSubscriptionsError: null,
      };
    case subscriptionActions.FETCH_USER_SUBSCRIPTIONS_SUCCESS:
      if (!action.payload) {
        console.error('Received empty payload in FETCH_USER_SUBSCRIPTIONS_SUCCESS');
        return state;
      }
      return {
        ...state,
        userSubscriptions: action.payload ? [action.payload] : [],
        userSubscriptionsLoading: false,
      };
    case subscriptionActions.FETCH_USER_SUBSCRIPTIONS_FAILURE:
      return {
        ...state,
        userSubscriptionsLoading: false,
        userSubscriptionsError: action.payload,
      };
    case subscriptionActions.FETCH_SUBSCRIPTIONS_INVOICES_SUCCESS:
      return {
        ...state,
        subscriptionInvoices: action.payload ? [action.payload] : [],
      };
    case subscriptionActions.FETCH_PAYMENT_STATUS:
      return {
        ...state,
        planSubscriptionStatus: action.payload ? [action.payload] : [],
      };
    case subscriptionActions.FETCH_PAYMENT_STATUS_SUCCESS:
      return {
        ...state,
        planSubscriptionStatus: action.payload ? [action.payload] : [],
        planSubscriptionStatusSuccess: true
      };
    case subscriptionActions.FETCH_PAYMENT_STATUS_FAILURE:
      return {
        ...state,
        planSubscriptionStatus: action.payload ? [action.payload] : [],
        planSubscriptionStatusSuccess: false
      };
    case subscriptionActions.FETCH_USER_SUBSCRIPTIONS_STATUS_SUCCESS:
      return {
        ...state,
        userSubscriptionStatus: action.payload ? [action.payload] : [],
        userSubscriptionStatusSuccess: true
      };
    case subscriptionActions.FETCH_USER_SUBSCRIPTIONS_STATUS_FAILURE:
      return {
        ...state,
        userSubscriptionStatus: action.payload ? [action.payload] : [],
        userSubscriptionStatusSuccess: false
      };
    default:
      return { ...state };
  }
};